// ABOUTME: Installation page for users who scan the QR code from desktop
// ABOUTME: Provides platform-specific instructions to add TradesPurple to home screen

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { Share, PlusCircle, ChevronDown, Smartphone, QrCode, CheckCircle, Download } from 'lucide-react';
import TradesPurpleLayout from '../components/Layout';

interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

export default function InstallPage() {
  const navigate = useNavigate();
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect platform
    const checkPlatform = () => {
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const android = /Android/i.test(navigator.userAgent);
      const isLargeScreen = window.innerWidth > 768;
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const desktop = isLargeScreen && !hasTouch;
      const tablet = isLargeScreen && hasTouch;

      setIsIOS(iOS);
      setIsAndroid(android);
      setIsTablet(tablet);
      setIsDesktop(desktop);
    };

    // Check if already installed as PWA
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                         (window.navigator as SafariNavigator).standalone === true;
      setIsStandalone(standalone);
    };

    checkPlatform();
    checkStandalone();

    // Also check on resize
    window.addEventListener('resize', checkPlatform);
    return () => window.removeEventListener('resize', checkPlatform);
  }, []);

  // Desktop view - redirect back
  if (isDesktop) {
    return (
      <TradesPurpleLayout>
        <Helmet>
          <title>Install TradesPurple - Scan with Your Phone</title>
        </Helmet>

        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Card className="w-full md:w-4/5 max-w-md border-2 border-purple-900 rounded-xl shadow-lg">
            <CardContent className="p-8 text-center">
              <QrCode className="h-16 w-16 text-purple-700 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-purple-900 mb-4">
                Scan with Your Phone
              </h1>
              <p className="text-gray-600 mb-6">
                Please scan the QR code with your mobile device to install TradesPurple
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-purple-700 hover:bg-purple-800 text-white"
              >
                Go to TradesPurple Website
              </Button>
            </CardContent>
          </Card>
        </div>
      </TradesPurpleLayout>
    );
  }

  // Already installed view
  if (isStandalone) {
    return (
      <TradesPurpleLayout>
        <Helmet>
          <title>TradesPurple - Already Installed</title>
        </Helmet>

        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <Card className="w-full md:w-4/5 max-w-md border-2 border-purple-900 rounded-xl shadow-lg">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-purple-900 mb-4">
                You're All Set!
              </h1>
              <p className="text-gray-600 mb-6">
                TradesPurple is already installed on your device
              </p>
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-purple-700 hover:bg-purple-800 text-white"
              >
                Open TradesPurple
              </Button>
            </CardContent>
          </Card>
        </div>
      </TradesPurpleLayout>
    );
  }

  // Main mobile installation view
  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>Install TradesPurple - Add to Home Screen</title>
        <meta name="description" content="Add TradesPurple to your home screen for easy access to trusted tradespeople" />
      </Helmet>

      <div className="flex flex-col">
        {/* Hero section */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Smartphone className="h-8 w-8 text-white" />
          </div>

          <h1 className="text-2xl font-bold text-purple-900 mb-3">
            Install TradesPurple
          </h1>

          <p className="text-base text-gray-600">
            Add to your home screen for easy access
          </p>
        </div>

        {/* Instructions card */}
        <Card className="w-full md:w-4/5 max-w-lg mx-auto border-2 border-purple-900 rounded-xl shadow-lg mb-6">
          <CardHeader className="text-center">
            <CardTitle className="text-lg text-purple-900">
              {isIOS ?
                (isTablet ? 'For iPad Users' : 'For iPhone Users') :
                (isTablet ? 'For Android Tablet Users' : 'For Android Users')
              }
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5">
            {isIOS ? (
              <div className="space-y-5">
                <InstallStep
                  number={1}
                  icon={<Share className="h-5 w-5" />}
                  title="Tap the Share button"
                  description={isTablet ? "Find it at the top of Safari" : "Find it at the bottom of Safari"}
                />

                <InstallStep
                  number={2}
                  icon={<PlusCircle className="h-5 w-5" />}
                  title="Scroll and tap 'Add to Home Screen'"
                  description="You might need to scroll down in the share menu"
                />

                <InstallStep
                  number={3}
                  icon={<Download className="h-5 w-5" />}
                  title="Tap 'Add' in the top right"
                  description="You can rename it if you'd like"
                />
              </div>
            ) : (
              <div className="space-y-5">
                <InstallStep
                  number={1}
                  icon={<ChevronDown className="h-5 w-5" />}
                  title="Tap the menu button"
                  description={isTablet ?
                    "Usually three dots (⋮) at the top right of your browser" :
                    "Usually three dots (⋮) in your browser"
                  }
                />

                <InstallStep
                  number={2}
                  icon={<PlusCircle className="h-5 w-5" />}
                  title={isTablet ?
                    "'Add to Home screen' or 'Install'" :
                    "'Add to Home screen' or 'Install app'"
                  }
                  description="The exact wording varies by browser (Chrome, Firefox, Samsung Internet)"
                />

                <InstallStep
                  number={3}
                  icon={<CheckCircle className="h-5 w-5" />}
                  title="Tap Install or Add"
                  description="Accept any permissions if asked"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits reminder */}
        <div className="bg-purple-50 rounded-lg p-4 mx-auto w-full max-w-lg mb-6">
          <p className="text-sm text-purple-700 text-center">
            💡 Once installed, TradesPurple works offline and feels just like a native app!
          </p>
        </div>

        {/* Browser-specific tips for Android */}
        {isAndroid && (
          <div className="border border-purple-200 rounded-lg p-4 mx-auto w-full max-w-lg mb-6">
            <p className="text-sm font-semibold text-purple-900 mb-2">Browser-specific tips:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>Chrome:</strong> Menu → "Add to Home screen"</li>
              <li>• <strong>Firefox:</strong> Menu → "Install"</li>
              <li>• <strong>Samsung Internet:</strong> Menu → "Add page to" → "Home screen"</li>
              <li>• <strong>Edge:</strong> Menu → "Add to phone"</li>
            </ul>
          </div>
        )}

        {/* Help section */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Having trouble?
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-purple-700 text-purple-700 hover:bg-purple-50"
          >
            Continue to Website
          </Button>
        </div>
      </div>
    </TradesPurpleLayout>
  );
}

// Install step component
interface InstallStepProps {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

function InstallStep({ number, icon, title, description }: InstallStepProps) {
  return (
    <div className="flex items-start space-x-3">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
          <span className="text-purple-700 font-bold text-sm">{number}</span>
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center mb-1">
          <div className="text-purple-700 mr-2">{icon}</div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
}
